import { Info } from 'lucide-react';

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@shigo-ui/ui';

type TargetingSummaryGroup = {
  label: string;
  items: string[];
};

export function TargetingSummaryMenu({
  groups = [],
  items = [],
  title,
}: {
  groups?: TargetingSummaryGroup[] | undefined;
  items?: string[] | undefined;
  title: string;
}) {
  const populatedGroups = groups.filter((group) => group.items.length);
  const itemCount =
    items.length + populatedGroups.reduce((count, group) => count + group.items.length, 0);
  if (!itemCount) {
    // Width matches the icon button for column alignment; no fixed height, so an empty value
    // keeps the shared compact row (only buttons and links get the table's negative margin).
    return <span className="inline-flex w-9 justify-center">-</span>;
  }
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9"
          aria-label={`${title}: ${String(itemCount)}`}
        >
          <Info className="h-4 w-4" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-72 min-w-56 overflow-y-auto">
        <DropdownMenuLabel>{title}</DropdownMenuLabel>
        {items.map((item, index) => (
          // Read-only labels that may repeat; the list is rebuilt, never reordered in place.
          // react-doctor-disable-next-line react-doctor/no-array-index-as-key
          <DropdownMenuItem key={`${item}-${String(index)}`} disabled>
            {item}
          </DropdownMenuItem>
        ))}
        {populatedGroups.map((group, groupIndex) => (
          <div key={group.label}>
            {(items.length > 0 || groupIndex > 0) && <DropdownMenuSeparator />}
            <DropdownMenuLabel className="text-muted-foreground text-xs">
              {group.label}
            </DropdownMenuLabel>
            {group.items.map((item, itemIndex) => (
              <DropdownMenuItem key={`${item}-${String(itemIndex)}`} disabled>
                {item}
              </DropdownMenuItem>
            ))}
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
